import React, { useState, useCallback } from 'react';
import GHGEmissionHeader from './GHGEmissionHeader';
import GHGEmissionTable from './GHGEmissionTable';
import './GHGEmissionDataManagement.scss';

const GHGEmissionDataManagement = () => {
  // Example state for filters and data
  const [filters, setFilters] = useState({
    periodFrom: '2025-01-01',
    periodTo: '2025-12-31',
    scope: 'Scope 1',
    category: [],
    businessUnitIds: [],
    period: '2025-01-01'
  });

  // Edit mode state management
  const [isEditMode, setIsEditMode] = useState(false);
  const [tableEdits, setTableEdits] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentView, setCurrentView] = useState('combined');
  
  // Table data state for save functionality
  const [tableData, setTableData] = useState({
    tableRows: [],
    branchNames: [],
    editableData: {},
    validationErrors: {}
  });
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const [clearEditableData, setClearEditableData] = useState(false);
  const [refreshTimestamp, setRefreshTimestamp] = useState(Date.now());

  // Handle edit mode changes
  const handleEditModeChange = useCallback((edit) => {
    setIsEditMode(edit);
    if (!edit) {
      setTableEdits({});
      setHasChanges(false);
      // Clear editable data when exiting edit mode
      setTableData(prev => ({
        ...prev,
        editableData: {}
      }));
    }
  }, []);

  // Handle table data changes
  const handleTableDataChange = useCallback((edits) => {
    setTableEdits(edits);
    
    // Check if there are actual changes (not just empty values)
    const hasActualChanges = Object.keys(edits).some(rowKey => {
      const rowData = edits[rowKey];
      const hasRowChanges = Object.keys(rowData).some(columnKey => {
        const value = rowData[columnKey];
        // Allow empty strings as valid changes (user might want to clear a value)
        // Also check for UOM changes (columns ending with _uom)
        const isValidValue = value !== undefined && value !== null;
        const isUOMChange = columnKey.endsWith('_uom') && value !== undefined && value !== null;
        return isValidValue || isUOMChange;
      });
      return hasRowChanges;
    });
    
    setHasChanges(hasActualChanges);
    
    // Update table data with new edits
    setTableData(prev => ({
      ...prev,
      editableData: edits
    }));
  }, [tableEdits]);

  // Handle table data update from table component
  const handleTableDataUpdate = useCallback((data) => {
    setTableData(data);
    const hasErrors = data.validationErrors && Object.keys(data.validationErrors).length > 0;
    setHasValidationErrors(hasErrors);
  }, []);

  // Handle save data
  const handleSaveData = useCallback((clearedEditableData = null) => {
    if (clearedEditableData !== null) {
      // This is called from the header after successful save
      setTableEdits(clearedEditableData);
      setHasChanges(false);
      setTableData(prev => ({
        ...prev,
        editableData: clearedEditableData
      }));
    }
  }, []);

  // Handle clear editable data
  const handleClearEditableData = useCallback(() => {
    setClearEditableData(true);
    // Force a complete refresh by updating timestamp
    setRefreshTimestamp(Date.now());
    // Reset after a short delay to allow the table to process it
    setTimeout(() => {
      setClearEditableData(false);
    }, 100);
  }, []);

  // Handle search change
  const handleSearchChange = useCallback((term) => {
    setSearchTerm(term);
  }, []);

  // Handle view change
  const handleViewChange = useCallback((view) => {
    setCurrentView(view);
  }, []);

  // Handle filter change
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);



  return (
    <div className="ghg-emission-data-management">
      
      <GHGEmissionHeader 
        onFilterChange={handleFilterChange} 
        onEditModeChange={handleEditModeChange}
        onSaveData={handleSaveData}
        isEditMode={isEditMode}
        hasChanges={hasChanges}
        onSearchChange={handleSearchChange}
        onViewChange={handleViewChange}
        editableData={tableData.editableData}
        tableRows={tableData.tableRows}
        branchNames={tableData.branchNames}
        tableEdits={tableEdits}
        tableData={tableData}
        onClearEditableData={handleClearEditableData}
        hasValidationErrors={hasValidationErrors}
      />
      <GHGEmissionTable 
        filters={filters} 
        isEditMode={isEditMode}
        onDataChange={handleTableDataChange}
        searchTerm={searchTerm}
        currentView={currentView}
        onTableDataUpdate={handleTableDataUpdate}
        clearEditableData={clearEditableData}
        refreshTimestamp={refreshTimestamp}
      />
    </div>
  );
};

export default GHGEmissionDataManagement; 